import * as React from 'react';
import DetailsSection from 'nomst/Components/SharedJobCollab/detailsSection';
import { mxStrings, compStrings, compNames } from 'utils/JobCollabStrings';
import CompFactorCard from 'nomst/Components/JobDetails/compFactorCard';
import './compFactorsDisplay.scss';

interface CompFactorsDisplayProps {
    compFactors: CompfactorDictionary;
    jobResponsibilities?: string[];
}

interface CompFactorDescriptor {
    key: string;
    important: boolean;
}

export const experienceFactors: string[] = [compNames.yearsExperience, compNames.yearsMinimum];
export const educationFactors: string[] = [compNames.highestDegreeEarned];
export const managementResponsibilityFactors: string[] = [
    compNames.managementRole,
    compNames.numberSupervised,
    compNames.peopleTypesManaged,
    compNames.peopleManagementTasks
];
// Factors that should be displayed prominently
export const importantFactors: string[] = [
    compNames.yearsExperience,
    compNames.yearsMinimum,
    compNames.highestDegreeEarned,
    compNames.managementRole
];

let friendlyNames: {[name: string]: string} = {};

class CompFactorsDisplay extends React.Component<CompFactorsDisplayProps, {}> {

    render(): React.ReactNode {
        const { jobResponsibilities } = this.props;

        friendlyNames = friendlyNames.length ? friendlyNames : Object.keys(compNames)
            .reduce((acc: {[name: string]: string}, name: string) => {
                acc[compNames[name]] = compStrings[name];
                return acc;
            }, {});

        const experienceDescriptors = this._buildDescriptors(experienceFactors);
        const educationDescriptors = this._buildDescriptors(educationFactors);
        const managementDescriptors =
            this._buildDescriptors(managementResponsibilityFactors);

        let jobDescriptors;
        if (jobResponsibilities) {
            const filteredJobResponsibilities = this._getFilteredJobResponsibilities();
            jobDescriptors = this._getGroupDescriptors(filteredJobResponsibilities);
        }

        if (!experienceDescriptors.length &&
            !educationDescriptors.length &&
            !managementDescriptors.length &&
            (!jobDescriptors || !jobDescriptors.length)) {
            return null;
        }

        return <DetailsSection
            title={mxStrings.jobDetailsTopCompFactors}
            className="comp-factors"
        >
            <div className="comp-factors-inner">
                { experienceDescriptors.length > 0 &&
                    this._renderCompFactorCard(
                        mxStrings.experience,
                        experienceDescriptors,
                        'experience') }
                { educationDescriptors.length > 0 &&
                    this._renderCompFactorCard(
                        mxStrings.education,
                        educationDescriptors,
                        'education') }
                { managementDescriptors.length > 0 &&
                    this._renderCompFactorCard(
                        mxStrings.managmentResponsibilities,
                        managementDescriptors,
                        'mgmt-resp') }
                { jobDescriptors && jobDescriptors.length > 0 &&
                    this._renderCompFactorCard(
                        mxStrings.jobResponsibilities,
                        jobDescriptors,
                        'job-resp') }
            </div>
        </DetailsSection>;
    }

    // Render a group of comp factors
    // where title is the name of the group and
    // cfDescriptors are keys for each comp factor in the group and whether it's displayed as 'important'
    private _renderCompFactorCard(
        title: string,
        cfDescriptors: CompFactorDescriptor[],
        className?: string,
    ): React.ReactNode {
        const { compFactors } = this.props;
        const items = cfDescriptors.filter(cfDescriptor => !!friendlyNames[cfDescriptor.key]).map(cfDescriptor => {
            let value = this._getCompFactorValue(compFactors, cfDescriptor.key);
            if (cfDescriptor.key === compNames.managementRole) {
                if (typeof(value) === 'boolean') {
                    value = value ? 'Yes' : 'No';
                }
            }
            return {
                factorName: cfDescriptor.key,
                label: friendlyNames[cfDescriptor.key],
                value,
                important: cfDescriptor.important
            };
        });

        return <CompFactorCard
            className={className}
            title={title}
            items={items}
        />;
    }

    private _getCompFactorValue(compFactors: CompfactorDictionary, key: string): CompfactorValue {
        const dictionaryValue = compFactors[key];
        if (dictionaryValue) {
            return dictionaryValue;
        }

        return '';
    }

    private _buildDescriptors(
        factors: string[]
    ): CompFactorDescriptor[] {
        return this._getGroupDescriptors(factors);
    }

    // Gets the descriptors for the given group of comp factors
    // ie, the key for each comp factor and whether or not it should be displayed as "important"
    private _getGroupDescriptors(group: string[]): CompFactorDescriptor[] {
        const { compFactors } = this.props;
        return group.filter(cfKey => !!compFactors[cfKey]).map(cfKey =>
            ({key: cfKey, important: importantFactors.indexOf(cfKey) !== -1}));
    }

    // Filter comp factors from other groups out of job responsibililties
    private _getFilteredJobResponsibilities(): string[] {
        const { jobResponsibilities } = this.props;
        return jobResponsibilities
            ? jobResponsibilities.filter(cfKey => managementResponsibilityFactors.indexOf(cfKey) === -1)
            : [];
    }
}

export { CompFactorsDisplay };
export default CompFactorsDisplay;